import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { take } from 'rxjs/operators';
import { CoinUser, Comment, SeasonPlan } from '@coin/shared/core/util-models';
import { CommentType, RecordState, SeasonProcessState } from '@coin/shared/core/util-enums';
import { DateHelper, TinyHelpers } from '@coin/shared/core/util-helpers';
import { SeasonRecordsCommentsService } from '@coin/shared/season-mgmt/data-access';
import { UserState } from '@coin/shared/auth/feature';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EditRecordHelper,
  EditRecordRequest,
  SeasonRecordPayoutStateEnum,
  SeasonRecordsUiMappingHelper
} from '@coin/shared/employee-data/util';
import { RecordAchievementDto, SeasonRecord } from '@coin/shared/incentive/util';
import { lastValueFrom } from 'rxjs';
import { IncentiveAchievementsService } from '../../../season-mgmt/services/incentive-achievements.service';
import { ProcessNavigationService } from '../../../season-mgmt/services/process-navigation.service';
import { SeasonState, SetCurrentPlan } from '../../../season-mgmt/store';

@Component({
  selector: 'app-employee-target-overview',
  templateUrl: './employee-target-overview.component.html',
  styleUrls: ['./employee-target-overview.component.scss'],
  standalone: false
})
export class EmployeeTargetOverviewComponent implements OnInit {
  private processNavigationService = inject(ProcessNavigationService);
  private store = inject(Store);
  private translateService = inject(TranslateService);
  private incentiveAchievement = inject(IncentiveAchievementsService);
  seasonRecordsCommentsService = inject(SeasonRecordsCommentsService);
  dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  @Input() record: SeasonRecord;
  @Input() showIncentiveData: boolean;
  readonly editable = input<boolean>(undefined);
  readonly isIncentiveAchievements = input<boolean>(undefined);
  readonly selectedPlan = input<SeasonPlan>(undefined);

  @Output() editItem = new EventEmitter<EditRecordRequest>();

  user$ = this.store.select<CoinUser>(UserState?.user);

  public seasonRecordPayoutStateEnum = SeasonRecordPayoutStateEnum;
  public recordState = RecordState;
  public commentType = CommentType;
  public recordAchievementTable: RecordAchievementDto;
  public recordComments: Comment[] = [];

  public getTranslatedKey(key: string): string {
    return this.translateService.instant(
      `season-mgmt.incentive.monitoring.records.${TinyHelpers.camelCaseToPascalCase(key)}`
    );
  }

  public displayRecordFieldFn(item: string): string {
    return TinyHelpers.pascalcaseToText(SeasonRecordsUiMappingHelper.mapValue(item));
  }

  ngOnInit(): void {
    this.setIncentiveTable();
    this.getRecordComments();
  }

  private getRecordComments(): void {
    this.seasonRecordsCommentsService
      .getComments(this.record?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(comments => {
        this.recordComments = comments.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });
  }

  private setIncentiveTable(): void {
    if (!this.isIncentiveAchievements()) {
      return;
    }

    this.incentiveAchievement
      .getRecordAchievements(this.record?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(achievement => {
        this.recordAchievementTable = achievement;
      });
  }

  public editPayoutState(): void {
    this.editItem.emit(EditRecordHelper.payoutState);
  }

  public editPayoutDate(): void {
    this.editItem.emit(EditRecordHelper.payoutDate);
  }

  public update(): void {
    this.setIncentiveTable();
  }

  public async goToPlan(): Promise<void> {
    const season = this.store.selectSnapshot(SeasonState.currentSeason);
    const planId = this.selectedPlan()?.id ?? this.record?.plan?.id;
    await lastValueFrom(
      this.store
        .dispatch(new SetCurrentPlan({ seasonId: season.id, seasonType: season.seasonType, planId }))
        .pipe(take(1))
    );
    this.processNavigationService.goToStep(SeasonProcessState.PlanDesign);
  }

  public checkDate(date: string): string {
    return date === DateHelper.dbMinDate ? '' : date;
  }

  public editIndividualMultiplierValue(): void {
    this.editItem.emit(EditRecordHelper.individualMultiplierAmount);
  }
}
